/**
 * TrustBadges Component
 * B2B compliance-safe trust signals for institutional laboratory supply.
 */

import React from 'react';
import { Shield, Award, FlaskConical, CheckCircle, Clock, Lock, Building2 } from 'lucide-react';

/**
 * Full Trust Badges Row - for product pages and checkout
 */
export const TrustBadgesRow = ({ className = '' }) => {
  const TRUST_BADGES = [
    {
      id: 'analytical-grade',
      icon: FlaskConical,
      title: 'Analytical Grade',
      description: 'High Assay Standard',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      id: 'technical-data',
      icon: Award,
      title: 'Technical Data Sheet',
      description: 'With Every Lot',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      id: 'b2b',
      icon: Building2,
      title: 'B2B Only',
      description: 'Verified Institutions',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      id: 'secure',
      icon: Lock,
      title: 'Secure Checkout',
      description: 'SSL Encrypted',
      color: 'text-slate-600',
      bgColor: 'bg-slate-100'
    }
  ];

  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${className}`}>
      {TRUST_BADGES.map((badge) => {
        const Icon = badge.icon;
        return (
          <div 
            key={badge.id}
            className={`${badge.bgColor} rounded-xl p-4 text-center`}
            data-testid={`trust-badge-${badge.id}`}
          >
            <Icon className={`w-6 h-6 ${badge.color} mx-auto mb-2`} />
            <p className={`font-semibold text-sm ${badge.color}`}>{badge.title}</p>
            <p className="text-xs text-slate-500 mt-0.5">{badge.description}</p>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Compact Trust Badges - for cart and smaller spaces
 */
export const TrustBadgesCompact = ({ className = '' }) => {
  const MINI_BADGES = [
    { id: 'verified', icon: CheckCircle, text: 'Analytical Grade' },
    { id: 'fast', icon: Clock, text: 'Institutional Fulfillment SLA' },
    { id: 'secure', icon: Shield, text: 'Secure Payment' }
  ];

  return (
    <div className={`flex flex-wrap items-center justify-center gap-4 ${className}`}>
      {MINI_BADGES.map((badge) => {
        const Icon = badge.icon;
        return (
          <div 
            key={badge.id}
            className="flex items-center gap-1.5 text-slate-600"
            data-testid={`mini-badge-${badge.id}`}
          >
            <Icon className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-medium">{badge.text}</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Trust Badge Strip - horizontal for headers/footers
 */
export const TrustBadgeStrip = ({ className = '' }) => {
  return (
    <div className={`flex items-center justify-center gap-6 py-3 bg-slate-50 border-y border-slate-100 ${className}`}>
      <div className="flex items-center gap-2">
        <FlaskConical className="w-4 h-4 text-emerald-600" />
        <span className="text-xs font-medium text-slate-700">Analytical Grade</span>
      </div>
      <div className="h-4 w-px bg-slate-200" />
      <div className="flex items-center gap-2">
        <Award className="w-4 h-4 text-blue-600" />
        <span className="text-xs font-medium text-slate-700">Technical Data Sheet Included</span>
      </div>
      <div className="h-4 w-px bg-slate-200" />
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-purple-600" />
        <span className="text-xs font-medium text-slate-700">B2B Institutional Supply</span>
      </div>
      <div className="h-4 w-px bg-slate-200 hidden md:block" />
      <div className="hidden md:flex items-center gap-2">
        <Shield className="w-4 h-4 text-slate-600" />
        <span className="text-xs font-medium text-slate-700">Secure Checkout</span>
      </div>
    </div>
  );
};

/**
 * Product Page Trust Section with institutional supply guarantees
 */
export const ProductTrustSection = ({ className = '' }) => {
  return (
    <div className={`bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 ${className}`}>
      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <Shield className="w-5 h-5 text-emerald-600" />
        Institutional Supply Standard
      </h3>
      
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-slate-900 text-sm">Analytical Grade</p>
            <p className="text-xs text-slate-500">Each lot independently characterized by chromatographic assay and mass verification</p>
          </div>
        </div>
        
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-slate-900 text-sm">Technical Data Sheet</p>
            <p className="text-xs text-slate-500">Lot-specific documentation supplied for institutional records</p>
          </div>
        </div>
        
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-slate-900 text-sm">Analytical Grade</p>
            <p className="text-xs text-slate-500">Manufactured in ISO-referenced institutional supply facilities</p>
          </div>
        </div>
        
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-slate-900 text-sm">Institutional Billing</p>
            <p className="text-xs text-slate-500">Purchase order and invoice options for verified institutional accounts</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrustBadgesRow;
